import React, { ChangeEvent, memo, useEffect, useState } from 'react';
import { ValidationObj } from '../../../types/interface';
import {
  capitalizeFirstLetter,
  checkValidValue,
  convertFromCurrency,
  convertToCurrency
} from 'utils/helpers';
import validationUtils from '../../utils/validationUtils';
import classNames from 'classnames';
import { Form } from 'react-bootstrap';
import { t } from 'i18next';
import { NumericFormat } from 'react-number-format';
import ConditionalRender from 'base/ConditionalRender/ConditionalRender';

interface Props {
  inputId?: string;
  type?: string;
  placeholder?: string;
  validations: ValidationObj[];
  label?: string;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  onChange: (value: number | null) => void;
  className: string;
  inputClassName?: string;
  helpText?: string;
  errorText?: string;
  error?: boolean;
  checkValidity?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareValue?: any;
  showError: boolean;
  afterIcon?: boolean;
  icon?: string;
  iconClass?: string;
  maxLength?: number;
  minLength?: number;
  name?: string;
  pattern?: RegExp;
  inputClasses?: string;
  getTooltip?: () => Promise<string>;
  tooltipText?: string;
  onKeyDown?: (e: any) => void;
  id?: string;
  max?: boolean;
  maxDate?: Date;
  minDate?: Date;
  accept?: string;
  defaultValue?: string;
  allowNegative?: boolean;
}

const CustomCurrencyInput = (props : Props) => {
  const [value, setValue] = useState<string | undefined>(undefined);
  const [valid, setValid] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [errorKey, setErrorKey] = useState<string | undefined>("");

  let firstTimeShownValue;
  useEffect(() => {
    firstTimeShownValue = convertToCurrency(props.value);
    if (checkValidValue(firstTimeShownValue) && checkValidValue(props.value)) {
      setValue(firstTimeShownValue);
    } else {
      setValue(undefined);
    }
  }, [props.value]);

  useEffect(() => {
    setDirty(false);
  }, [props.label, props.placeholder]);

  useEffect(() => {
    checkValidity(props.value);
  }, [
    props.compareValue,
    props.validations,
    dirty,
    errorText,
    props.value,
    props.showError,
  ]);

  useEffect(() => {
    if (checkValidValue(props.value) && props.allowNegative) {
      return;
    } else {
      const newNonNegetiveValue = checkValidValue(props.value)
        ? Number(Math.abs(props.value as number).toFixed(2))
        : null;
      const convertedValue = checkValidValue(newNonNegetiveValue)
        ? convertToCurrency(newNonNegetiveValue?.toFixed(2) as string)
        : null;
      setValue(convertedValue! as string);
      props.onChange(newNonNegetiveValue);
    }
  }, [props.allowNegative]);

  const checkValidity = (value: string | number | undefined | null) => {
    let isValid = true;
    let errorText = "",
      errorKey: string | undefined = "";
    for (let validation of props.validations || []) {
      if (validation.takeZeroasValue) {
        if (typeof value === "number" && value === 0 ) {
          continue;
        } else if (!validationUtils[validation.type](value, validation)) {
          errorText = validation.message;
          errorKey = validation.key;
          isValid = false;
          break;
        }
      }
      if (validation.type !== "checkFormValidity") {
        if (
          !validation.disabled &&
          !validationUtils[validation.type](value, validation)
        ) {
          errorText = validation.message;
          errorKey = validation.key;
          isValid = false;
          break;
        }
      }
    }
    setValid(isValid);
    setErrorText(errorText);
    setErrorKey(errorKey);
  };

  const getStandardInputClasses = () => {
    const { disabled, inputClassName } = props;
    return classNames(
      {
        disabled: disabled,
        error: (dirty || props.showError) && !valid,
      },
      "standard-input",
      "position-relative ",
      `${inputClassName}`
    );
  };

  const getInputClassName = () => {
    return classNames(
      "mb-0 mt-0 ps-2 form-control position-relative w-100",
      props.iconClass,
      props.inputClasses
    );
  };

  const renderLabel = () => {
    const { label } = props;

    if (label) {
      return (
        <div className="fs-14 capitalize">
          {capitalizeFirstLetter(label)}{' '}
          <ConditionalRender
            condition={!!props.validations && props.validations.length > 0}
          >
            <span className="text-danger fs-15">*</span>
          </ConditionalRender>
        </div>
      );
    }
    return null;
  };

  const renderHelp = () => {
    const { helpText } = props;

    if (helpText) {
      return <Form.Text className="text-muted">{helpText}</Form.Text>;
    }
    return null;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
    if (e.keyCode === 13) {
      e.preventDefault();
    }
    if (props.type === "number") {
      if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
        e.preventDefault();
      }
    }
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setDirty(true);
  };

  const renderError = () => {
    if (errorText && (dirty || props.showError)) {
      return (
        <Form.Text className="text-danger error standard-error-text">
          {errorKey ? capitalizeFirstLetter(t(errorKey)) : errorText}
        </Form.Text>
      );
    }
    return null;
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const originalValue = convertFromCurrency(value);
    if (e.target.value !== "") {
      window.formIsDirty = true;
    } else {
      window.formIsDirty = false;
    }

    if (checkValidValue(originalValue)) {
      props.onChange(originalValue);
    } else {
      props.onChange(originalValue ?? null); // Uses nullish coalescing to handle null/undefined cases
    }

    checkValidity(originalValue);
  };

  return (
    <div className={`${props.className} standard-input position-relative my-2`}>
      {renderLabel()}
      <div className={getStandardInputClasses()} id="currency">
          <div className="standard-currency">
            <NumericFormat
              thousandSeparator="."
              decimalSeparator=","
              prefix="€"
              decimalScale={2}
              allowNegative={!!props.allowNegative}
              displayType="input"
              className={getInputClassName()}
              onChange={onChange}
              value={value}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              title={props.label}
              placeholder={""}
              disabled={props.disabled}
              data-bs-toggle={"tooltip"}
              data-bs-placement={"top"}
            />
          </div>
      </div>
      <div className="grid-div-3">{valid ? renderHelp() : renderError()}</div>
    </div>
  );
};

export default CustomCurrencyInput;
export const CurrencyInput = React.memo(CustomCurrencyInput);
export const StandardCurrency = React.memo(CustomCurrencyInput);
